import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { X, Users, UserPlus, Loader2, Search } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const resolvePic = (pic) => {
  if (!pic) return null;
  if (pic.startsWith('http')) return pic;
  if (pic.startsWith('/api/')) return `${process.env.REACT_APP_BACKEND_URL}${pic}`;
  return pic;
};

/** Modal that lists a user's Followers / Following with a search filter and
 *  inline avatars. Tapping a row navigates to that user's profile.
 */
export default function FollowersModal({
  open,
  onClose,
  userId,
  initialTab = 'followers',
  followersCount = 0,
  followingCount = 0,
}) {
  const [tab, setTab] = useState(initialTab);
  const [followers, setFollowers] = useState([]);
  const [following, setFollowing] = useState([]);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState('');

  useEffect(() => {
    if (!open) return;
    setTab(initialTab);
  }, [open, initialTab]);

  useEffect(() => {
    if (!open || !userId) return;
    let cancel = false;
    (async () => {
      setLoading(true);
      try {
        const url = tab === 'followers'
          ? `${API}/social/users/${userId}/followers?limit=200`
          : `${API}/social/users/${userId}/following?limit=200`;
        const res = await fetch(url, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token') || ''}` },
        });
        if (res.ok && !cancel) {
          const d = await res.json();
          if (tab === 'followers') setFollowers(d.followers || []);
          else setFollowing(d.following || []);
        }
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => { cancel = true; };
  }, [open, userId, tab]);

  if (!open) return null;

  const list = tab === 'followers' ? followers : following;
  const q = query.trim().toLowerCase();
  const filtered = q
    ? list.filter((u) => (u.name || '').toLowerCase().includes(q))
    : list;

  return (
    <div
      className="fixed inset-0 z-[80] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="followers-modal"
    >
      <div
        className="w-full max-w-md max-h-[85vh] flex flex-col rounded-3xl bg-[#13153A] border border-white/10 shadow-[0_20px_60px_-20px_rgba(168,85,247,0.5)] overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 pt-5 pb-3">
          <h3 className="text-white font-syne font-bold text-lg">
            {tab === 'followers' ? 'Followers' : 'Following'}
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center"
            data-testid="followers-modal-close"
            aria-label="Close"
          >
            <X size={16} />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-1.5 px-4 pb-3">
          <button
            type="button"
            onClick={() => setTab('followers')}
            className={`flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-full text-xs font-bold transition-all ${
              tab === 'followers'
                ? 'bg-gradient-to-r from-fuchsia-500/30 to-cyan-500/30 border border-fuchsia-400/50 text-white shadow-[0_0_18px_rgba(236,72,153,0.22)]'
                : 'bg-white/5 border border-white/10 text-zinc-300 hover:bg-white/10'
            }`}
            data-testid="followers-tab-followers"
          >
            <Users size={13} /> Followers · <span className="opacity-80">{followersCount}</span>
          </button>
          <button
            type="button"
            onClick={() => setTab('following')}
            className={`flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-full text-xs font-bold transition-all ${
              tab === 'following'
                ? 'bg-gradient-to-r from-fuchsia-500/30 to-cyan-500/30 border border-fuchsia-400/50 text-white shadow-[0_0_18px_rgba(236,72,153,0.22)]'
                : 'bg-white/5 border border-white/10 text-zinc-300 hover:bg-white/10'
            }`}
            data-testid="followers-tab-following"
          >
            <UserPlus size={13} /> Following · <span className="opacity-80">{followingCount}</span>
          </button>
        </div>

        {/* Search */}
        <div className="px-4 pb-3">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={`Search ${tab === 'followers' ? 'followers' : 'following'}…`}
              className="w-full pl-9 pr-3 py-2 rounded-xl bg-white/5 border border-white/10 text-white text-sm placeholder:text-zinc-500 focus:outline-none focus:border-fuchsia-400/40"
              data-testid="followers-search"
            />
          </div>
        </div>

        {/* List */}
        <div className="flex-1 overflow-y-auto px-2 pb-3 min-h-[160px]">
          {loading ? (
            <div className="flex items-center justify-center py-10">
              <Loader2 className="animate-spin text-fuchsia-400" size={18} />
            </div>
          ) : filtered.length === 0 ? (
            <div className="px-4 py-10 text-center">
              <p className="text-zinc-300 text-sm font-semibold">
                {q
                  ? 'No matches.'
                  : tab === 'followers'
                    ? 'No followers yet.'
                    : 'Not following anyone yet.'}
              </p>
              <p className="text-zinc-500 text-xs mt-1">
                {q ? 'Try a different name.' : 'They will show up here as they arrive.'}
              </p>
            </div>
          ) : (
            <ul className="space-y-1">
              {filtered.map((u) => {
                const href = u.vendor_id ? `/vendor/${u.vendor_id}` : null;
                const pic = resolvePic(u.profile_image);
                const Avatar = (
                  pic ? (
                    <img
                      src={pic}
                      alt=""
                      className="w-10 h-10 rounded-full object-cover border border-white/10 flex-shrink-0"
                    />
                  ) : (
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-fuchsia-500/40 to-cyan-500/40 flex items-center justify-center text-white font-semibold text-sm flex-shrink-0 border border-white/10">
                      {(u.name || '?')[0]}
                    </div>
                  )
                );
                const Body = (
                  <>
                    {Avatar}
                    <div className="min-w-0 flex-1">
                      <p className="text-white text-sm font-semibold truncate">{u.name || 'Member'}</p>
                      <p className="text-zinc-400 text-[11px] flex items-center gap-1.5">
                        {u.role === 'vendor' ? 'Vendor' : u.role === 'supporter' ? 'Supporter' : 'Member'}
                        {u.vendor_id && <span className="text-fuchsia-300">· View profile →</span>}
                      </p>
                    </div>
                  </>
                );
                return (
                  <li key={u.user_id}>
                    {href ? (
                      <Link
                        to={href}
                        onClick={onClose}
                        className="flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-white/5 transition-colors"
                        data-testid={`followers-row-${u.user_id}`}
                      >
                        {Body}
                      </Link>
                    ) : (
                      <div
                        className="flex items-center gap-3 px-3 py-2.5 rounded-xl"
                        data-testid={`followers-row-${u.user_id}`}
                      >
                        {Body}
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
